"use client";

import { memo, useState, useMemo, ReactNode } from "react";
import { ChevronDown, ChevronRight, Database, BarChart3, ExternalLink } from "lucide-react";
import { Markdown } from "@/components/markdown";
import dynamic from "next/dynamic";

const Plot = dynamic(() => import("react-plotly.js"), { ssr: false });

interface DataFrameInfo {
  data: Array<Record<string, any>>;
  schema: {
    columns: string[];
    types: Record<string, string>;
    row_count: number;
  };
  sources?: Array<{ name: string; url: string }>;
  reasoning: string;
}

interface SubAgentSlideProps {
  task: string;
  summary?: string;
  dataframes: Record<string, DataFrameInfo>;
  charts?: Array<{ plotly_json: any; title?: string }>;
  functions: string[];
}

// ── Citation tag patterns ──
// [df:some_name] → inline DataFrame
// [vs:Some Chart Title — with special chars] → inline chart
const CITATION_RE = /\[(df|vs):([^\]]+)\]/g;

type Segment =
  | { type: "md"; text: string }
  | { type: "df"; name: string }
  | { type: "vs"; title: string };

function parseSummarySegments(summary: string): Segment[] {
  const segments: Segment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const re = new RegExp(CITATION_RE.source, "g");

  while ((match = re.exec(summary)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: "md", text: summary.slice(lastIndex, match.index) });
    }
    const tag = match[1] as "df" | "vs";
    const value = match[2];
    if (tag === "df") {
      segments.push({ type: "df", name: value });
    } else {
      segments.push({ type: "vs", title: value });
    }
    lastIndex = re.lastIndex;
  }
  if (lastIndex < summary.length) {
    segments.push({ type: "md", text: summary.slice(lastIndex) });
  }
  return segments;
}

// ── Value formatting ──

function formatValue(value: any): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number") {
    if (Number.isInteger(value) && Math.abs(value) >= 1000)
      return value.toLocaleString();
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(value);
}

function getRowSources(row: Record<string, any>): string[] {
  const src = row.source;
  if (!src) return [];
  if (Array.isArray(src)) return src.filter(Boolean);
  if (typeof src === "string" && src.trim()) return [src];
  return [];
}

// ── Inline collapsible DataFrame ──

function InlineDataTable({ df, name }: { df: DataFrameInfo; name: string }) {
  const [open, setOpen] = useState(false);
  const visibleColumns = useMemo(
    () => df.schema.columns.filter((col) => col !== "source"),
    [df.schema.columns]
  );
  const hasSourceColumn = df.schema.columns.includes("source");
  const maxRows = 15;
  const displayData = df.data.slice(0, maxRows);

  return (
    <div className="my-2 rounded-lg border border-border/20 bg-muted/5 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-muted/10 transition-colors"
      >
        {open ? (
          <ChevronDown className="w-3 h-3 text-muted-foreground/50 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-3 h-3 text-muted-foreground/50 flex-shrink-0" />
        )}
        <Database className="w-3 h-3 text-blue-400/60 flex-shrink-0" />
        <span className="text-[11px] font-mono font-medium text-foreground/70">
          {name}
        </span>
        <span className="text-[10px] text-muted-foreground/40 font-mono ml-auto">
          {df.schema.row_count} rows × {df.schema.columns.length} cols
        </span>
      </button>
      {open && (
        <div className="px-2 pb-2 overflow-x-auto border-t border-border/10">
          <table className="w-full text-[11px] mt-1">
            <thead>
              <tr className="bg-muted/15 border-b border-border/15">
                {hasSourceColumn && <th className="w-5 px-1.5 py-1" />}
                {visibleColumns.map((col) => (
                  <th
                    key={col}
                    className="px-1.5 py-1 text-left font-semibold text-foreground/55 whitespace-nowrap uppercase text-[9px] tracking-[0.08em]"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/8">
              {displayData.map((row, idx) => {
                const sources = getRowSources(row);
                return (
                  <tr
                    key={idx}
                    className={`hover:bg-muted/8 ${sources.length > 0 ? "cursor-pointer" : ""}`}
                    onClick={() =>
                      sources.length > 0 &&
                      sources.forEach((url) =>
                        window.open(url, "_blank", "noopener,noreferrer")
                      )
                    }
                  >
                    {hasSourceColumn && (
                      <td className="w-5 px-1.5 py-1">
                        {sources.length > 0 && (
                          <ExternalLink className="w-2.5 h-2.5 text-blue-400/70" />
                        )}
                      </td>
                    )}
                    {visibleColumns.map((col) => (
                      <td
                        key={col}
                        className="px-1.5 py-1 text-foreground/80 whitespace-nowrap tabular-nums font-light"
                      >
                        {formatValue(row[col])}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {df.data.length > maxRows && (
            <p className="text-[10px] text-muted-foreground/40 pl-1 mt-1">
              Showing {maxRows} of {df.data.length} rows
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Detect multi-subplot charts that need more height ──

function hasSubplots(plotlyJson: any): boolean {
  if (!plotlyJson) return false;
  const layout = plotlyJson.layout || {};
  // Check for xaxis2/yaxis2+ keys (Plotly subplot convention)
  return Object.keys(layout).some((k) => /^[xy]axis[2-9]/.test(k));
}

// ── Inline collapsible Chart ──

function InlineChart({ chart, label }: { chart: { plotly_json: any; title?: string }; label: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="my-2 rounded-lg border border-border/20 bg-muted/5 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-muted/10 transition-colors"
      >
        {open ? (
          <ChevronDown className="w-3 h-3 text-muted-foreground/50 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-3 h-3 text-muted-foreground/50 flex-shrink-0" />
        )}
        <BarChart3 className="w-3 h-3 text-blue-400/60 flex-shrink-0" />
        <span className="text-[11px] font-mono font-medium text-foreground/70 truncate">
          {label}
        </span>
      </button>
      {open && (
        <div className="border-t border-border/10 p-2">
          <Plot
            data={chart.plotly_json?.data || []}
            layout={{
              ...(chart.plotly_json?.layout || {}),
              autosize: true,
              margin: { t: 60, r: 30, b: 60, l: 70 },
              paper_bgcolor: "transparent",
              plot_bgcolor: "transparent",
              font: { color: "#999", size: 11 },
            }}
            config={{ displayModeBar: false, responsive: true }}
            style={{ width: "100%", height: hasSubplots(chart.plotly_json) ? "650px" : "420px" }}
            useResizeHandler
          />
        </div>
      )}
    </div>
  );
}

// ── Main component ──

function SubAgentSlideComponent({
  task,
  summary,
  dataframes,
  charts,
  functions,
}: SubAgentSlideProps) {
  const chartList = charts || [];
  const dfMap = dataframes || {};


  // Build a lookup for charts by normalized title for [vs:...] matching
  const chartByTitle = useMemo(() => {
    const map = new Map<string, { plotly_json: any; title?: string }>();
    for (const c of chartList) {
      if (c.title) {
        map.set(c.title.toLowerCase().trim(), c);
      }
    }
    return map;
  }, [chartList]);

  // Parse summary into segments with inline citations resolved
  const segments = useMemo(() => {
    if (!summary) return [];
    return parseSummarySegments(summary);
  }, [summary]);

  // Track which charts/dfs were cited inline so we can show uncited ones at the end
  const citedDfs = useMemo(() => {
    const set = new Set<string>();
    for (const s of segments) {
      if (s.type === "df") set.add(s.name);
    }
    return set;
  }, [segments]);

  const citedChartTitles = useMemo(() => {
    const set = new Set<string>();
    for (const s of segments) {
      if (s.type === "vs") set.add(s.title.toLowerCase().trim());
    }
    return set;
  }, [segments]);

  const uncitedDfs = useMemo(
    () => Object.entries(dfMap).filter(([name]) => !citedDfs.has(name)),
    [dfMap, citedDfs]
  );
  const uncitedCharts = useMemo(
    () => chartList.filter((c) => !c.title || !citedChartTitles.has(c.title.toLowerCase().trim())),
    [chartList, citedChartTitles]
  );

  if (!summary && chartList.length === 0 && Object.keys(dfMap).length === 0) {
    return (
      <div className="text-sm text-muted-foreground/50 py-6 text-center">
        No data returned.
      </div>
    );
  }

  // Render segments: markdown chunks + inline citations
  const renderSegments = (): ReactNode[] => {
    return segments.map((seg, i) => {
      if (seg.type === "md") {
        return (
          <div
            key={`md-${i}`}
            className="text-[13px] leading-relaxed text-foreground/90 prose prose-sm prose-invert max-w-none
              prose-headings:text-foreground/95 prose-headings:font-semibold
              prose-h1:text-base prose-h2:text-[14px] prose-h3:text-[13px]
              prose-p:mb-2 prose-p:leading-relaxed
              prose-strong:text-foreground prose-strong:font-semibold
              prose-li:py-0 prose-ul:my-1 prose-ol:my-1
              prose-table:my-2 prose-th:text-[10px] prose-td:text-[11px]
              prose-hr:border-border/20 prose-hr:my-3"
          >
            <Markdown>{seg.text}</Markdown>
          </div>
        );
      }

      if (seg.type === "df") {
        const df = dfMap[seg.name];
        if (!df) {
          return (
            <span key={`df-miss-${i}`} className="text-[11px] text-muted-foreground/50 font-mono">
              [df:{seg.name} — not found]
            </span>
          );
        }
        return <InlineDataTable key={`df-${i}`} df={df} name={seg.name} />;
      }

      if (seg.type === "vs") {
        const chart = chartByTitle.get(seg.title.toLowerCase().trim());
        if (!chart) {
          return (
            <span key={`vs-miss-${i}`} className="text-[11px] text-muted-foreground/50 font-mono">
              [vs:{seg.title} — not found]
            </span>
          );
        }
        return <InlineChart key={`vs-${i}`} chart={chart} label={seg.title} />;
      }

      return null;
    });
  };

  return (
    <div className="w-full space-y-0">
      {renderSegments()}

      {/* Render any uncited charts/DFs at the bottom */}
      {(uncitedCharts.length > 0 || uncitedDfs.length > 0) && (
        <div className="pt-2 space-y-1">
          {uncitedCharts.map((chart, j) => (
            <InlineChart key={`uc-chart-${j}`} chart={chart} label={chart.title || `Chart ${j + 1}`} />
          ))}
          {uncitedDfs.map(([name, df]) => (
            <InlineDataTable key={`uc-df-${name}`} df={df} name={name} />
          ))}
        </div>
      )}
    </div>
  );
}

export const SubAgentSlide = memo(SubAgentSlideComponent);
